// The quote of the day on Home: a short quote from a well-known person, changing every day (Riyadh date), the same
// for everyone in the same kind of work that day. A fixed list (no AI service); only quotes with a well-established
// author (no "internet" misattributions). A role's own quotes are mixed with the general ones and the list is walked
// in order, so a quote comes back only after all the others.

export type MotivationKind = 'sales' | 'presales' | 'projects';
export interface Quote { text: string; by: string }

const q = (text: string, by: string): Quote => ({ text, by });

const GENERAL: Quote[] = [
  q('Saudi ambition is like Jabal Tuwaiq.', 'Crown Prince Mohammed bin Salman'),
  q('Well done is better than well said.', 'Benjamin Franklin'),
  q('Genius is one percent inspiration and ninety-nine percent perspiration.', 'Thomas Edison'),
  q('The only way to do great work is to love what you do.', 'Steve Jobs'),
  q('Knowing is not enough; we must apply. Willing is not enough; we must do.', 'Johann Wolfgang von Goethe'),
  q('Strive not to be a success, but rather to be of value.', 'Albert Einstein'),
  q('Life is like riding a bicycle. To keep your balance you must keep moving.', 'Albert Einstein'),
  q('Do what you can, with what you have, where you are.', 'Theodore Roosevelt'),
  q('Far and away the best prize that life offers is the chance to work hard at work worth doing.', 'Theodore Roosevelt'),
  q('We are what we repeatedly do. Excellence, then, is not an act, but a habit.', 'Will Durant'),
  q('Talent wins games, but teamwork and intelligence win championships.', 'Michael Jordan'),
  q('Hard work beats talent when talent doesn’t work hard.', 'Tim Notke'),
  q('Lost time is never found again.', 'Benjamin Franklin'),
  q('Energy and persistence conquer all things.', 'Benjamin Franklin'),
  q('Great things are done by a series of small things brought together.', 'Vincent van Gogh'),
  q('Alone we can do so little; together we can do so much.', 'Helen Keller'),
  q('If you want to go fast, go alone. If you want to go far, go together.', 'African proverb'),
  q('The best way to predict the future is to invent it.', 'Alan Kay'),
  q('Efficiency is doing things right; effectiveness is doing the right things.', 'Peter Drucker'),
  q('Victory belongs to the most persevering.', 'Napoleon Bonaparte'),
  q('The best preparation for tomorrow is doing your best today.', 'H. Jackson Brown Jr.'),
  q('The difference between ordinary and extraordinary is that little extra.', 'Jimmy Johnson'),
  q('Setting goals is the first step in turning the invisible into the visible.', 'Tony Robbins'),
  q('You don’t have to be great to start, but you have to start to be great.', 'Zig Ziglar'),
  q('The harder the conflict, the more glorious the triumph.', 'Thomas Paine'),
];

const SALES: Quote[] = [
  q('You miss 100% of the shots you don’t take.', 'Wayne Gretzky'),
  q('The purpose of a business is to create a customer.', 'Peter Drucker'),
  q('Your most unhappy customers are your greatest source of learning.', 'Bill Gates'),
  q('Approach each customer with the idea of helping him solve a problem, not of selling a product.', 'Brian Tracy'),
  q('People don’t buy for logical reasons. They buy for emotional reasons.', 'Zig Ziglar'),
  q('Every sale has five basic obstacles: no need, no money, no hurry, no desire, no trust.', 'Zig Ziglar'),
  q('Opportunities multiply as they are seized.', 'Sun Tzu'),
  q('I’ve failed over and over and over again in my life. And that is why I succeed.', 'Michael Jordan'),
  q('Champions keep playing until they get it right.', 'Billie Jean King'),
  q('Stay hungry, stay foolish.', 'Steve Jobs'),
];

const PRESALES: Quote[] = [
  q('Chance favors only the prepared mind.', 'Louis Pasteur'),
  q('Quality is never an accident; it is always the result of intelligent effort.', 'John Ruskin'),
  q('Measure twice, cut once.', 'Proverb'),
  q('Plans are worthless, but planning is everything.', 'Dwight D. Eisenhower'),
  q('Well begun is half done.', 'Aristotle'),
  q('Perseverance is not a long race; it is many short races one after the other.', 'Walter Elliot'),
  q('Knowing is not enough; we must apply.', 'Johann Wolfgang von Goethe'),
  q('Opportunities multiply as they are seized.', 'Sun Tzu'),
];

const PROJECTS: Quote[] = [
  q('The journey of a thousand miles begins with a single step.', 'Lao Tzu'),
  q('Plans are worthless, but planning is everything.', 'Dwight D. Eisenhower'),
  q('Quality is never an accident; it is always the result of intelligent effort.', 'John Ruskin'),
  q('Measure twice, cut once.', 'Proverb'),
  q('Well begun is half done.', 'Aristotle'),
  q('Great things are done by a series of small things brought together.', 'Vincent van Gogh'),
  q('Perseverance is not a long race; it is many short races one after the other.', 'Walter Elliot'),
  q('Chance favors only the prepared mind.', 'Louis Pasteur'),
];

/** Today's date in Riyadh as a day number, so the quote changes at midnight Riyadh time. */
function riyadhDay(now = new Date()): number {
  const ymd = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Riyadh', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
  const [y, m, d] = ymd.split('-').map(Number);
  return Math.floor(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) / 86_400_000);
}

/** Today's quote for this kind of work: the role's own quotes and the general ones, taken in turn. */
export function dailyMotivation(kind: MotivationKind, now = new Date()): Quote {
  const own = kind === 'presales' ? PRESALES : kind === 'projects' ? PROJECTS : SALES;
  const list: Quote[] = [];
  for (let i = 0; i < Math.max(own.length, GENERAL.length); i++) {
    if (i < own.length) list.push(own[i]!);
    if (i < GENERAL.length && !list.some((x) => x.text === GENERAL[i]!.text)) list.push(GENERAL[i]!);
  }
  return list[riyadhDay(now) % list.length]!;
}
